import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { useSlottingStore } from "@/lib/slotting/store";
import {
  activeStatusDotRules,
  unionStatusDots,
} from "@/lib/slotting/statusDots";
import type { StatusDotRule } from "@/lib/slotting/types";

/**
 * Row of small colored status dots for an appointment/request surface. Renders
 * nothing when no rule matches. Native `title` tooltips on purpose: the tiles
 * are tiny absolutely-positioned blocks inside scroll containers where a
 * portal tooltip isn't warranted for a label list.
 */
export function StatusDots({
  dots,
  size = 2,
  max = 4,
  className = "inline-flex items-center gap-0.5",
}: {
  dots: StatusDotRule[];
  /** Tailwind size step: 1.5 → h-1.5/w-1.5 (dense tiles), 2 → h-2/w-2. */
  size?: 1.5 | 2;
  max?: number;
  className?: string;
}) {
  const t = useTranslations("slotting");
  if (dots.length === 0) return null;
  const visible = dots.slice(0, max);
  const overflow = dots.slice(max);
  const sizeClass = size === 1.5 ? "h-1.5 w-1.5" : "h-2 w-2";
  return (
    <span className={className}>
      {visible.map((dot) => (
        <span
          key={dot.id}
          className={`inline-block rounded-full shrink-0 ${sizeClass}`}
          style={{ backgroundColor: dot.color }}
          title={dot.label || t("statusDotRuleUnnamed")}
        />
      ))}
      {overflow.length > 0 && (
        <span
          className="text-[9px] leading-none font-secondary shrink-0"
          title={overflow.map((d) => d.label || t("statusDotRuleUnnamed")).join("\n")}
        >
          +{overflow.length}
        </span>
      )}
    </span>
  );
}

const NO_DOTS: StatusDotRule[] = [];

/**
 * Store-connected: matched status-dot rules for the given guests (union across
 * guests on multi-guest tiles, rule order preserved). Cheap when the offering
 * has no rules. `guestIds` should be reference-stable or memoized by the
 * caller where render pressure matters (tiles).
 */
export function useGuestStatusDots(guestIds: string[]): StatusDotRule[] {
  const rules = useSlottingStore((s) => s.offering?.statusDotRules);
  const activeRules = useMemo(() => activeStatusDotRules(rules), [rules]);
  // Subscribe to just the requested guests' records (shallow-compared), so a
  // merge touching OTHER guests doesn't re-render this tile.
  const guests = useSlottingStore(
    useShallow((s) =>
      activeRules.length === 0 || guestIds.length === 0
        ? NO_DOTS_GUESTS
        : guestIds
            .map((id) => s.guestRecords.get(id))
            .filter((g): g is NonNullable<typeof g> => Boolean(g)),
    ),
  );
  return useMemo(() => {
    if (activeRules.length === 0 || guests.length === 0) return NO_DOTS;
    return unionStatusDots(activeRules, guests);
  }, [activeRules, guests]);
}

const NO_DOTS_GUESTS: never[] = [];
